import React, { useEffect, useState } from 'react';
import { Upload } from 'lucide-react';
import API from '../services/API';
import DateSelect from './DateSelect';

const CUR_YEAR = new Date().getFullYear();

/**
 * Popup BẮT BUỘC bổ sung thông tin — hiện ngay sau login nếu thiếu field.
 * Danh sách field do admin cấu hình tại /login-required-fields (bật/tắt + ghi chú).
 * KHÔNG có nút "Để sau" — phải điền đủ mới vào app được.
 * Fail-open: nếu API lỗi → không chặn user.
 */
const MissingInfoModal = ({ accessToken }) => {
  const [missing, setMissing]             = useState(null);   // null=chưa check, []=ok, [...]= thiếu
  const [values, setValues]               = useState({});
  const [cities, setCities]               = useState([]);
  const [saving, setSaving]               = useState(false);
  const [avatarPreview, setAvatarPreview] = useState('');

  useEffect(() => {
    if (!accessToken) return;
    (async () => {
      try {
        // Song song: config bắt buộc (NoAuth) + profile user
        const [requiredFields, profile] = await Promise.all([
          API.getLoginRequiredFields().catch(() => []),
          API.getMyAgentProfile(accessToken).catch(() => null),
        ]);

        // Fail-open: lỗi API → không chặn
        if (!requiredFields.length || !profile) { setMissing([]); return; }

        const miss = requiredFields.filter(f => {
          const v = profile[f.field_key];
          return !v || String(v).trim() === '';
        });

        setMissing(miss);
        if (miss.some(f => f.field_type === 'city')) {
          API.getCities().then(l => setCities(Array.isArray(l) ? l : [])).catch(() => {});
        }
      } catch {
        setMissing([]); // fail-open
      }
    })();
  }, [accessToken]);

  // Chưa check xong → không render (tránh flash)
  if (missing === null || missing.length === 0) return null;

  const setVal = (k, v) => setValues(p => ({ ...p, [k]: v }));

  const handleAvatar = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => setAvatarPreview(ev.target.result);
    reader.readAsDataURL(file);
    const up = await API.uploadImage(file).catch(() => null);
    if (up?.success) { setVal('avatar', up.fileUrl); setAvatarPreview(up.fileUrl); }
  };

  // Chỉ cho save khi điền đủ tất cả field hiển thị
  const allFilled = missing.every(f => {
    const v = values[f.field_key];
    return v !== undefined && String(v).trim() !== '';
  });

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      missing.forEach(f => {
        if (values[f.field_key] !== undefined && values[f.field_key] !== '') payload[f.field_key] = values[f.field_key];
      });
      if (Object.keys(payload).length > 0) await API.updateProfileBasic(payload, accessToken);
      setMissing([]); // đóng modal
    } catch (e) {
      alert(e.message || 'Lưu thất bại, vui lòng thử lại');
    } finally {
      setSaving(false);
    }
  };

  const inputCls = 'w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-[16px] focus:ring-2 focus:ring-brand-500 outline-none';

  return (
    <div className="fixed inset-0 z-[1000] bg-black/60 flex items-end sm:items-center justify-center p-3">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto animate-slide-up">
        {/* Không có nút X — modal chỉ đóng khi save thành công */}
        <div className="sticky top-0 bg-gradient-to-r from-brand-600 to-emerald-500 text-white px-5 py-4 rounded-t-3xl">
          <h2 className="font-bold text-lg">Bổ sung thông tin bắt buộc</h2>
          <p className="text-brand-50 text-xs mt-0.5">Vui lòng điền đủ để tiếp tục sử dụng app</p>
        </div>

        <div className="p-5 space-y-4">
          {missing.map(f => (
            <div key={f.field_key}>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">{f.label}</label>
              {f.note && <p className="text-xs text-gray-400 mb-1.5">{f.note}</p>}
              {f.field_type === 'text' && (
                <input value={values[f.field_key] || ''} onChange={e => setVal(f.field_key, e.target.value)} className={inputCls} />
              )}
              {f.field_type === 'date' && (
                <DateSelect
                  value={values[f.field_key] || ''}
                  onChange={v => setVal(f.field_key, v)}
                  yearFrom={f.field_key === 'joinDate' ? 2010 : 1940}
                  yearTo={CUR_YEAR}
                />
              )}
              {f.field_type === 'city' && (
                <select value={values[f.field_key] || ''} onChange={e => setVal(f.field_key, e.target.value)} className={inputCls}>
                  <option value="">Chọn tỉnh thành</option>
                  {cities.map(c => <option key={c.id || c.code || c.name} value={c.name}>{c.name}</option>)}
                </select>
              )}
              {f.field_type === 'avatar' && (
                <>
                  <input type="file" accept="image/*" onChange={handleAvatar} className="hidden" id="missing-avatar" />
                  <label htmlFor="missing-avatar" className="flex items-center justify-center w-full h-24 rounded-2xl border-2 border-dashed border-brand-200 bg-brand-50/40 cursor-pointer">
                    {avatarPreview
                      ? <img src={avatarPreview} alt="avatar" className="w-20 h-20 rounded-full object-cover" />
                      : <div className="text-center text-gray-500"><Upload className="w-6 h-6 mx-auto mb-1 text-brand-400" /><span className="text-sm">Tải ảnh lên</span></div>}
                  </label>
                </>
              )}
            </div>
          ))}

          <button
            onClick={save}
            disabled={saving || !allFilled}
            className="w-full bg-brand-600 text-white py-3 rounded-xl font-semibold hover:bg-brand-700 disabled:opacity-40 disabled:cursor-not-allowed mt-2"
          >
            {saving ? 'Đang lưu...' : 'Lưu & tiếp tục'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MissingInfoModal;
